"use client";

import React, { useCallback, useEffect } from "react";
import styled from "styled-components";
import { Flex, Icon, useBoolean, Text } from "@thenounproject/lingo-core";

import { Container } from "../_components/Elements";
import { TextButton } from "./buttons";
import { MARKETING_BP_MD } from "./utils/constants";
import { NAV_LINKS } from "./header/Header";

const LINGO_SECTION = {
  title: "Lingo",
  links: [
    { text: "Home", link: "/" },
    NAV_LINKS.about,
    { text: "Legal", link: "/legal" },
    { text: "Accessibility", link: "/accessibility" },
  ],
};

const RESOURCES_SECTION = {
  title: "Resources",
  links: [
    NAV_LINKS.blog,
    NAV_LINKS.pricing,
    NAV_LINKS.glossary,
    { text: "Sitemap", link: "/sitemap" },
    NAV_LINKS.help,
    { text: "Status", link: "https://status.lingoapp.com" },
    NAV_LINKS.contact,
  ],
};

const MAIN_SECTIONS = [
  {
    title: "Product",
    links: [
      NAV_LINKS.portals,
      NAV_LINKS.kits,
      NAV_LINKS.library,
      NAV_LINKS.userManagement,
      NAV_LINKS.fileConversion,
      NAV_LINKS.directLinks,
      NAV_LINKS.insights,
      NAV_LINKS.metadata,
      NAV_LINKS.visualWorkspace,
      NAV_LINKS.integrations,
    ],
  },
  {
    title: "How We Help",
    links: [
      NAV_LINKS.brandGuidelines,
      NAV_LINKS.dam,
      NAV_LINKS.brandPortals,
      NAV_LINKS.salesEnablement,
      NAV_LINKS.googleDrive,
      NAV_LINKS.dropbox,
    ],
  },
  {
    title: "Customers",
    links: [
      NAV_LINKS.brandTeams,
      NAV_LINKS.agencies,
      NAV_LINKS.smallMediumBusinesses,
      NAV_LINKS.enterprise,
    ],
  },
];

const SOCIAL_LINKS = [
  {
    href: "https://www.facebook.com/Lingoapp/",
    title: "Facebook",
    iconId: "social.facebook",
  },
  {
    href: "https://www.instagram.com/lingo_app/",
    title: "Instagram",
    iconId: "social.instagram",
  },
  {
    href: "https://www.linkedin.com/company/lingoapp/",
    title: "LinkedIn",
    iconId: "social.linkedin",
  },
];

const FooterContent = styled(Flex)`
  width: 100%;
  max-width: 1000px;
  gap: 50px;

  @media (max-width: ${MARKETING_BP_MD}) {
    flex-direction: column;
    gap: 40px;
  }
`;

const LeftColumn = styled(Flex)`
  flex-direction: column;
  gap: 40px;
  min-width: 206px;
`;

const RightColumns = styled(Flex)`
  flex: 1;
  gap: 50px;

  @media (max-width: ${MARKETING_BP_MD}) {
    flex-direction: column;
    gap: 40px;
  }
`;

const FooterSection = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const SectionTitle = styled(Text)`
  margin-bottom: 8px;
`;

const LinkList = styled(Flex)`
  flex-direction: column;
  gap: 8px;
`;

const FooterLink = styled(TextButton).attrs({})`
  display: block;
  width: fit-content;
  & > span {
    font-weight: 500;
  }
`;

const Logo = styled(Icon).attrs({
  iconId: "logo.full",
  width: "207px",
  fill: "grayLightest",
})`
  cursor: default;
  margin-bottom: 20px;
`;

const Copyright = styled(Text)`
  font-size: 12px;
`;

const CopyrightLink = styled(TextButton)`
  & > span {
    font-size: 12px;
    font-weight: 500;
  }
`;

const DebugFeature = ({
  children,
}: {
  children: (onClick: (e: React.MouseEvent<SVGElement>) => void) => React.ReactNode;
}) => {
  const [debugError, enableDebug] = useBoolean(false);

  const handleDebugClick = useCallback(
    (e: React.MouseEvent<SVGElement>) => {
      if (e.altKey && e.shiftKey) {
        enableDebug();
      }
    },
    [enableDebug]
  );

  useEffect(() => {
    if (debugError) {
      throw Error("Sorry, we're out of burritos.");
    }
  }, [debugError]);

  return <>{children(handleDebugClick)}</>;
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <Container
      background="primary"
      mt="none"
      mb="none"
      as="footer"
      py="44px"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
      variations={{
        "mq.m": {
          px: "40px",
        },
      }}>
      <Flex justifyContent="flex-start" alignItems="center" width="100%" maxWidth="1000px">
        <DebugFeature>{onClick => <Logo onClick={onClick} />}</DebugFeature>
      </Flex>
      <FooterContent>
        <LeftColumn>
          <FooterSection>
            <SectionTitle color="white" font="ui.regularBold">
              {LINGO_SECTION.title}
            </SectionTitle>
            <LinkList>
              {LINGO_SECTION.links.map(link => (
                <FooterLink
                  font="marketing.regular"
                  key={link.text}
                  href={link.link}
                  variant="white">
                  {link.text}
                </FooterLink>
              ))}
            </LinkList>
          </FooterSection>
          <FooterSection>
            <SectionTitle color="white" font="ui.regularBold">
              {RESOURCES_SECTION.title}
            </SectionTitle>
            <LinkList>
              {RESOURCES_SECTION.links.map(link => (
                <FooterLink
                  font="marketing.regular"
                  key={link.text}
                  href={link.link}
                  variant="white">
                  {link.text}
                </FooterLink>
              ))}
            </LinkList>
          </FooterSection>
        </LeftColumn>
        <RightColumns>
          {MAIN_SECTIONS.map(section => (
            <FooterSection key={section.title}>
              <SectionTitle color="white" font="ui.regularBold">
                {section.title}
              </SectionTitle>
              <LinkList>
                {section.links.map(link => (
                  <FooterLink
                    font="marketing.regular"
                    key={link.text}
                    href={link.link}
                    variant="white">
                    {link.text}
                  </FooterLink>
                ))}
              </LinkList>
            </FooterSection>
          ))}
        </RightColumns>
      </FooterContent>

      <Flex
        mt="xl"
        alignItems="center"
        justifyContent="space-between"
        maxWidth="1000px"
        variations={{
          "mq.s": { flexDirection: "column-reverse", gap: "24px" },
        }}
        width="100%">
        <Copyright color="white">
          &copy; {currentYear} Lingo&trade; Made by{" "}
          <CopyrightLink target="_blank" small href="https://thenounproject.com" variant="white">
            Noun Project
          </CopyrightLink>
        </Copyright>
        <Flex alignItems="center">
          {SOCIAL_LINKS.map(link => (
            <a
              href={link.href}
              key={link.title}
              title={link.title}
              aria-label={link.title}
              target="_blank"
              rel="noopener noreferrer"
              role="button">
              <Icon iconId={link.iconId} fill="white" size={32} mx="xs" />
            </a>
          ))}
        </Flex>
      </Flex>
    </Container>
  );
}
